import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import "./game.css";
import timerIcon from "../images/timer.png";
import scoreIcon from "../images/score.png";
import { syncJoinLobbyFromUrlSearch } from "../../utils/joinLobbyFromUrl";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";
const GAME_PROGRESS_KEY = "mythFactGameProgress";

function gameProgressScopeKey(userId, sessionId, organizationId, lobby) {
  return [
    String(userId || "").trim(),
    String(sessionId || "").trim(),
    String(organizationId || "").trim(),
    String(lobby || "").trim(),
  ].join("|");
}

function readGameProgress(scopeKey) {
  if (!scopeKey) return null;
  try {
    const raw = sessionStorage.getItem(GAME_PROGRESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.scopeKey !== scopeKey) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeGameProgress(scopeKey, snapshot) {
  if (!scopeKey || !snapshot) return;
  try {
    sessionStorage.setItem(
      GAME_PROGRESS_KEY,
      JSON.stringify({ ...snapshot, scopeKey, savedAt: Date.now() })
    );
  } catch {
    // ignore storage failures
  }
}

function clearGameProgress() {
  try {
    sessionStorage.removeItem(GAME_PROGRESS_KEY);
  } catch {
    // ignore storage failures
  }
}

/** Rebuild deck in saved order; drops ids that no longer exist in the fetched list. */
function restoreDeckFromSavedIds(questionList, deckIds) {
  if (!Array.isArray(deckIds) || !deckIds.length || !Array.isArray(questionList)) {
    return null;
  }
  const byId = new Map(
    questionList.map((q) => [String(q?.id ?? "").trim(), q]).filter(([id]) => id)
  );
  const restored = deckIds
    .map((id) => byId.get(String(id ?? "").trim()))
    .filter(Boolean);
  if (restored.length !== deckIds.length || !restored.length) return null;
  return restored;
}

/** Time to show correct/incorrect reveal (and confetti) before auto-advancing. */
const FEEDBACK_BEFORE_ADVANCE_MS = {
  correct: 4600,
  wrong: 3800,
  other: 3200,
};

/** Scoring off: no confetti / no correct-wrong styling — brief pause then next question. */
const FEEDBACK_ADVANCE_SCORING_OFF_MS = 1000;

const HOW_TO_PLAY_RULES = [
  "Read the situation on screen",
  "Pick the option you relate to the most",
  "Trust your instincts, there's no going back after selecting",
  "Complete all situations to finish the game",
  "Score high and finish fast to climb the leaderboard",
];

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function resolveLobbyName(locationState) {
  const fromState =
    typeof locationState?.lobbyName === "string"
      ? locationState.lobbyName.trim()
      : "";
  if (fromState) return fromState;
  try {
    const fromUrl = syncJoinLobbyFromUrlSearch(window.location.search);
    if (fromUrl) return fromUrl;
  } catch {
    // ignore
  }
  try {
    const ss = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (ss && ss.trim()) return ss.trim();
  } catch {
    // ignore storage failures
  }
  return "";
}

function decodeHtmlEntities(text) {
  if (text == null) return "";
  const ta = document.createElement("textarea");
  ta.innerHTML = String(text);
  return ta.value;
}

function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Lighter confetti on touch / narrow / low-spec devices to avoid frame drops. */
function resolveConfettiProfile() {
  if (prefersReducedMotion()) {
    return { waves: 0, piecesPerWave: 0, useShadow: false, simpleAnim: true, cleanupMs: 0 };
  }

  let mobileLike = false;
  try {
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.matchMedia("(max-width: 768px)").matches;
    const lowMemory =
      typeof navigator.deviceMemory === "number" && navigator.deviceMemory <= 4;
    const lowCores =
      typeof navigator.hardwareConcurrency === "number" &&
      navigator.hardwareConcurrency <= 4;
    mobileLike = (coarse && narrow) || lowMemory || lowCores;
  } catch {
    mobileLike = window.innerWidth <= 768;
  }

  if (mobileLike) {
    return {
      waves: 5,
      piecesPerWave: 14,
      useShadow: false,
      simpleAnim: true,
      cleanupMs: 3000,
    };
  }

  return {
    waves: 9,
    piecesPerWave: 22,
    useShadow: true,
    simpleAnim: false,
    cleanupMs: 4200,
  };
}

function spawnConfettiPieces(root, count, colors, delayOffsetMs, profile) {
  const fragment = document.createDocumentFragment();

  for (let i = 0; i < count; i += 1) {
    const piece = document.createElement("div");
    const w = profile.simpleAnim ? 7 + Math.random() * 10 : 7 + Math.random() * 14;
    const h = Math.random() > 0.35 ? w * (0.55 + Math.random() * 0.35) : w;
    const leftPct = 6 + Math.random() * 88;
    const topPct = 8 + Math.random() * 32;
    piece.style.position = "absolute";
    piece.style.left = `${leftPct}vw`;
    piece.style.top = `${topPct}vh`;
    piece.style.width = `${w}px`;
    piece.style.height = `${h}px`;
    const c = colors[Math.floor(Math.random() * colors.length)];
    piece.style.background = c;
    if (profile.useShadow) {
      piece.style.boxShadow =
        "0 0 10px rgba(255,255,255,0.45), 0 2px 6px rgba(0,0,0,0.2)";
    }
    piece.style.opacity = "1";
    piece.style.borderRadius = `${Math.random() > 0.45 ? 3 : 999}px`;
    piece.style.transform = `rotate(${Math.random() * 360}deg)`;
    piece.style.willChange = "transform, opacity";
    piece.style.contain = "layout style paint";

    const dx = (Math.random() - 0.5) * (profile.simpleAnim ? 220 : 260 + Math.random() * 380);
    const dy = profile.simpleAnim ? 220 + Math.random() * 180 : 280 + Math.random() * 360;
    const rot = (Math.random() - 0.5) * (profile.simpleAnim ? 280 : 520);
    const dur = profile.simpleAnim ? 1200 + Math.random() * 500 : 1450 + Math.random() * 950;
    const delay = delayOffsetMs + Math.random() * 55;

    const keyframes = profile.simpleAnim
      ? [
          {
            transform: "translate3d(0,0,0) rotate(0deg)",
            opacity: 1,
            offset: 0,
          },
          {
            transform: `translate3d(${dx}px, ${dy}px, 0) rotate(${rot}deg)`,
            opacity: 0,
            offset: 1,
          },
        ]
      : [
          {
            transform: "translate3d(0,0,0) rotate(0deg) scale(1)",
            opacity: 1,
            offset: 0,
          },
          {
            transform: `translate3d(${dx * 0.35}px, ${dy * 0.25}px, 0) rotate(${rot * 0.35}deg) scale(1.05)`,
            opacity: 1,
            offset: 0.35,
          },
          {
            transform: `translate3d(${dx * 0.7}px, ${dy * 0.65}px, 0) rotate(${rot * 0.7}deg) scale(1)`,
            opacity: 0.92,
            offset: 0.72,
          },
          {
            transform: `translate3d(${dx}px, ${dy}px, 0) rotate(${rot}deg) scale(0.9)`,
            opacity: 0,
            offset: 1,
          },
        ];

    piece.animate(keyframes, {
      duration: dur,
      delay,
      easing: profile.simpleAnim ? "ease-out" : "cubic-bezier(0.22, 0.61, 0.36, 1)",
      fill: "forwards",
    });
    fragment.appendChild(piece);
  }

  root.appendChild(fragment);
}

function fireConfettiBurst() {
  try {
    const profile = resolveConfettiProfile();
    if (profile.waves <= 0 || profile.piecesPerWave <= 0) return;

    const root = document.createElement("div");
    root.setAttribute("aria-hidden", "true");
    root.style.position = "fixed";
    root.style.left = "0";
    root.style.top = "0";
    root.style.width = "100vw";
    root.style.height = "100vh";
    root.style.pointerEvents = "none";
    root.style.zIndex = "10000";
    root.style.overflow = "hidden";
    root.style.contain = "strict";
    root.style.isolation = "isolate";

    const colors = [
      "#3be477",
      "#ffd84a",
      "#ff4d8d",
      "#7c5cff",
      "#ffffff",
      "#5dffc4",
      "#ffb347",
      "#e8d5ff",
      "#f4c542",
    ];

    let wave = 0;
    const spawnWave = () => {
      if (wave >= profile.waves || !root.isConnected) return;
      spawnConfettiPieces(root, profile.piecesPerWave, colors, wave * 20, profile);
      wave += 1;
      if (wave < profile.waves) {
        window.requestAnimationFrame(spawnWave);
      }
    };

    document.body.appendChild(root);
    window.requestAnimationFrame(spawnWave);
    window.setTimeout(() => {
      try {
        root.remove();
      } catch {}
    }, profile.cleanupMs);
  } catch {
    // ignore
  }
}

function rowOptionTexts(row) {
  return [
    String(row?.option_one ?? "").trim(),
    String(row?.option_two ?? "").trim(),
    String(row?.option_three ?? "").trim(),
    String(row?.option_four ?? "").trim(),
  ];
}

/** 0–3 index of correct option, or null if not set (no scoring). */
function correctAnswerIndex(row) {
  const opts = rowOptionTexts(row);
  const aid = parseInt(String(row?.answer_id ?? "").trim(), 10);
  if (aid >= 1 && aid <= 4 && opts[aid - 1]) return aid - 1;
  const ca = String(row?.correct_answer ?? "").trim();
  if (ca) {
    const i = opts.findIndex((o) => o && o === ca);
    if (i >= 0) return i;
  }
  if (opts[0] && opts[1] && !opts[2] && !opts[3]) {
    if (aid === 2) return 1;
    if (aid === 1) return 0;
  }
  return null;
}

function rowPopupAt(row, idx) {
  const info = String(row?.information ?? "").trim();
  if (idx === 0) {
    return String(row?.option_one_popup ?? row?.mythpopup ?? info ?? "").trim();
  }
  if (idx === 1) {
    return String(row?.option_two_popup ?? row?.factpopup ?? info ?? "").trim();
  }
  return info;
}

function explainHtmlForReveal(row, correctIdx0) {
  const information = String(row?.information ?? "").trim();
  const primary =
    correctIdx0 != null && correctIdx0 >= 0 && correctIdx0 <= 3
      ? rowPopupAt(row, correctIdx0)
      : "";
  const fallback = information;
  return decodeHtmlEntities(String(information || primary || fallback || "").trim());
}

function formatElapsed(ms) {
  const totalSeconds = Math.max(0, Math.floor(Number(ms || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/** ISO-style timestamp in Indian Standard Time (Asia/Kolkata, UTC+05:30). */
function formatIndianTimestamp(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}.${ms}+05:30`;
}

function toBool(v) {
  if (v === true || v === 1) return true;
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "y";
}

function toPositiveInt(v, fallback = 0) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  const i = Math.floor(n);
  return i > 0 ? i : fallback;
}

function normalizePlayerName(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.name;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t && !/^null(\s+null)*$/i.test(t)) return t;
  }
  const fn = user.firstName ?? user.first_name ?? "";
  const ln = user.lastName ?? user.last_name ?? "";
  const parts = [fn, ln].filter(
    (p) =>
      p != null &&
      String(p).trim() !== "" &&
      String(p).trim().toLowerCase() !== "null"
  );
  return parts.map((p) => String(p).trim()).join(" ").trim();
}

/** Stages table stores `question_timer` as MM:SS (e.g. 00:20) or seconds as a number. */
function parseQuestionTimerToSeconds(value) {
  if (value == null) return 0;
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.floor(value);
  }
  const s = String(value).trim();
  if (!s || s === "00:00") return 0;
  const mmss = /^(\d{1,3}):(\d{2})$/.exec(s);
  if (mmss) {
    const minutes = parseInt(mmss[1], 10);
    const sec = parseInt(mmss[2], 10);
    if (!Number.isFinite(minutes) || !Number.isFinite(sec)) return 0;
    return Math.max(0, minutes * 60 + sec);
  }
  const n = Number(s);
  if (Number.isFinite(n) && n > 0) return Math.floor(n);
  return 0;
}

function stageQuestionPoint(value, fallback = 10) {
  if (value == null || value === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.trunc(n));
}

/** When `point_status` is missing (legacy rows), treat scoring as on. */
function pointsEnabledFromStage(st) {
  if (!st || typeof st !== "object") return true;
  const raw = st.point_status;
  if (raw == null || String(raw).trim() === "") return true;
  return toBool(raw);
}

function shuffleQuestions(items) {
  const arr = Array.isArray(items) ? [...items] : [];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Permutation of [0,1,2,3] for option button colors; differs from `prev` when possible. */
function shuffleOptionPalette(prev) {
  const next = [0, 1, 2, 3];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  if (prev && prev.length === 4 && next.every((v, idx) => v === prev[idx])) {
    [next[0], next[1]] = [next[1], next[0]];
  }
  return next;
}

const OPTION_PALETTE_CLASSES = ["quiz-mc-pal-0", "quiz-mc-pal-1", "quiz-mc-pal-2", "quiz-mc-pal-3"];

function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);

  const [loading, setLoading] = useState(true);
  const [deck, setDeck] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [points, setPoints] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [isCommitting, setIsCommitting] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [startedAtMs, setStartedAtMs] = useState(() => Date.now());
  /** Always use forward session clock. */
  const [, setForwardClockTick] = useState(0);
  /** From `stages` via fetchGameQuestions — per-question timer length and points. */
  const [stageRules, setStageRules] = useState(null);
  const reportIdRef = useRef(null);
  const stageRowIdRef = useRef(null);
  const replayGuardDoneRef = useRef(false);
  const initPromiseRef = useRef(null);
  const demoRedirectTriggeredRef = useRef(false);
  const feedbackTimerRef = useRef(null);
  const pendingCompleteOnRestoreRef = useRef(false);

  const [answerLog, setAnswerLog] = useState([]);
  const answerLogRef = useRef([]);
  const [answerFeedback, setAnswerFeedback] = useState(null); // { selectedIdx, correctIdx0, status: 'correct'|'wrong'|'noanswer'|'timeout' }
  /** Maps each answer slot (0–3) to a palette index; reshuffled after each answered question. */
  const [optionPaletteOrder, setOptionPaletteOrder] = useState([0, 1, 2, 3]);
  const [questionEnterClass, setQuestionEnterClass] = useState("");
  const skipQuestionEnterAnimRef = useRef(true);
  const skipPaletteShuffleRef = useRef(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const storedUserData = useMemo(() => readStoredUserData(), []);
  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );
  const lobbyName = useMemo(
    () => resolveLobbyName(location.state),
    [location.state]
  );
  const effectiveLobbyName = useMemo(() => {
    const fromRoute = String(lobbyName || "").trim();
    if (fromRoute) return fromRoute;
    const fromUser = String(storedUserData?.lobby || "").trim();
    return fromUser;
  }, [lobbyName, storedUserData]);
  const gameProgressScope = useMemo(() => {
    const userId =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    return gameProgressScopeKey(
      userId,
      storedUserData.sessionId,
      storedUserData.organizationId,
      effectiveLobbyName
    );
  }, [
    storedUserData.userId,
    storedUserData.userid,
    storedUserData.id,
    storedUserData.sessionId,
    storedUserData.organizationId,
    effectiveLobbyName,
  ]);
  const lockedGamePath = useMemo(() => {
    const p = String(location.pathname || "/game");
    const s = String(location.search || "");
    const h = String(location.hash || "");
    return `${p}${s}${h}`;
  }, [location.pathname, location.search, location.hash]);
  useEffect(() => {
    const storedRid = String(
      storedUserData?.reportId || storedUserData?.reportid || ""
    ).trim();
    if (storedRid) {
      reportIdRef.current = storedRid;
    }
  }, [storedUserData]);
  const reportRoleForApi =
    storedUserData.user_type ||
    storedUserData.userType ||
    storedUserData.role ||
    (storedUserData.source === "DEMO" ? "demobypass" : null) ||
    storedUserData.source ||
    "ORG_USER";
  const sidLc = String(storedUserData?.sessionId ?? "").toLowerCase();
  const oidLc = String(storedUserData?.organizationId ?? "").toLowerCase();
  const isDemoBypass =
    String(reportRoleForApi).toLowerCase() === "demobypass" ||
    storedUserData?.source === "DEMO" ||
    (sidLc === "demobypass" && oidLc === "demobypass");

  const persistIdsToStoredUserData = useCallback((nextReportId, nextStageId) => {
    try {
      const raw =
        localStorage.getItem("userData") ||
        sessionStorage.getItem("userData") ||
        "{}";
      const parsed = JSON.parse(raw) || {};
      const merged = {
        ...parsed,
        ...(nextReportId ? { reportId: String(nextReportId) } : {}),
        ...(nextStageId ? { stageId: String(nextStageId) } : {}),
      };
      localStorage.setItem("userData", JSON.stringify(merged));
      sessionStorage.setItem("userData", JSON.stringify(merged));
    } catch {
      // ignore storage failures
    }
  }, []);

  const ensureStageAndReportReady = useCallback(async () => {
    if (isDemoBypass || !adminToken || !backendBase) return;
    if (initPromiseRef.current) {
      await initPromiseRef.current;
      return;
    }

    const uid =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    const pinRaw = String(
      new URLSearchParams(window.location.search).get("pin") || ""
    ).trim();
    const pinNum = Number(pinRaw);
    const loginPinBody =
      pinRaw !== "" && Number.isFinite(pinNum)
        ? { login_pin: Math.trunc(pinNum) }
        : {};
    if (!uid || !sid || !oid) return;

    const run = (async () => {
      try {
        let currentStageId = stageRowIdRef.current;
        let currentReportId = reportIdRef.current;

        // 1) Try to read current stage/report from backend first.
        try {
          const fr = await fetch(`${backendBase}/fetchReport`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId: sid,
              organizationId: oid,
              lobby: effectiveLobbyName || "",
            }),
          });
          const frData = await fr.json().catch(() => ({}));
          const stageIdFromFetch = frData?.report?.id;
          if (stageIdFromFetch != null && String(stageIdFromFetch).trim() !== "") {
            currentStageId = String(stageIdFromFetch).trim();
            stageRowIdRef.current = currentStageId;
          }
          const reportIdFromFetch = frData?.report?.reportId;
          if (reportIdFromFetch != null && String(reportIdFromFetch).trim() !== "") {
            currentReportId = String(reportIdFromFetch).trim();
            reportIdRef.current = currentReportId;
          }
        } catch (fetchErr) {
          console.error("game init fetchReport:", fetchErr);
        }

        // 2) Ensure/add stage row and force backend lobby-template sync for timer/points.
        // addContent is idempotent and on existing rows it also aligns timer_status/question_timer/question_point.
        try {
          const addRes = await fetch(`${backendBase}/addContent`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId: sid,
              organizationId: oid,
              email: storedUserData.email || "",
              emp_id: storedUserData.employeeId || storedUserData.emp_id || "",
              name: normalizePlayerName(storedUserData),
              reportId: currentReportId || storedUserData.reportId || storedUserData.reportid || "",
              gameId: storedUserData.gameId || "",
              token: storedUserData.token || "",
              user_type: reportRoleForApi,
              lobby: effectiveLobbyName || "",
              ...loginPinBody,
            }),
          });
          const addData = await addRes.json().catch(() => ({}));
          if (addRes.ok && addData?.success) {
            if (addData?.id != null && String(addData.id).trim() !== "") {
              currentStageId = String(addData.id).trim();
              stageRowIdRef.current = currentStageId;
            }
            const ridFromAdd = addData?.reportId;
            if (ridFromAdd != null && String(ridFromAdd).trim() !== "") {
              currentReportId = String(ridFromAdd).trim();
              reportIdRef.current = currentReportId;
            }
          }
        } catch (addErr) {
          console.error("game init addContent:", addErr);
        }

        // 3) Re-read reportId if addContent did not return one (existing row race).
        if (!currentReportId) {
          try {
            const fr2 = await fetch(`${backendBase}/fetchReport`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId: uid,
                sessionId: sid,
                organizationId: oid,
                lobby: effectiveLobbyName || "",
              }),
            });
            const fr2Data = await fr2.json().catch(() => ({}));
            const rid2 = fr2Data?.report?.reportId;
            if (rid2 != null && String(rid2).trim() !== "") {
              currentReportId = String(rid2).trim();
              reportIdRef.current = currentReportId;
            }
          } catch (reFetchErr) {
            console.error("game init fetchReport after addContent:", reFetchErr);
          }
        }

        persistIdsToStoredUserData(currentReportId, currentStageId);
      } catch (err) {
        console.error("game init stage/report:", err);
      } finally {
        initPromiseRef.current = null;
      }
    })();

    initPromiseRef.current = run;
    await run;
  }, [
    isDemoBypass,
    adminToken,
    backendBase,
    storedUserData,
    reportRoleForApi,
    effectiveLobbyName,
    persistIdsToStoredUserData,
    effectiveLobbyName,
  ]);

  useEffect(() => {
    dispatch(setBackButtonUrl(lockedGamePath || "/game"));
  }, [dispatch, lockedGamePath]);

  useEffect(() => {
    // Keep user on game page when browser back button is pressed repeatedly.
    window.history.pushState({ gameLock: true, step: 1 }, "", lockedGamePath);
    window.history.pushState({ gameLock: true, step: 2 }, "", lockedGamePath);
    const handlePopState = () => {
      window.history.pushState({ gameLock: true, step: 2 }, "", lockedGamePath);
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [lockedGamePath]);

  /* ── Body: class hooks game.css backgrounds (begin.jpg / begin_mob.jpg) ── */
  useEffect(() => {
    const body = document.body;
    const prevBg = body.style.backgroundImage;
    const prevBgSize = body.style.backgroundSize;
    const prevBgPos = body.style.backgroundPosition;
    const prevBgRepeat = body.style.backgroundRepeat;
    const prevBgAttachment = body.style.backgroundAttachment;
    const prevBgColor = body.style.backgroundColor;
    const prevClasses = new Set(Array.from(body.classList));

    body.classList.add("game-active");
    body.classList.remove("common-bg", "common-bg1", "rules-bg");
    // Let stylesheet set photo background; clear inline so CSS can apply.
    body.style.backgroundImage = "";
    body.style.backgroundSize = "";
    body.style.backgroundPosition = "";
    body.style.backgroundRepeat = "";
    body.style.backgroundAttachment = "";
    body.style.backgroundColor = "";

    return () => {
      body.classList.remove("game-active");
      body.className = "";
      prevClasses.forEach((c) => body.classList.add(c));
      body.style.backgroundImage = prevBg;
      body.style.backgroundSize = prevBgSize;
      body.style.backgroundPosition = prevBgPos;
      body.style.backgroundRepeat = prevBgRepeat;
      body.style.backgroundAttachment = prevBgAttachment;
      body.style.backgroundColor = prevBgColor;
    };
  }, []);

  useEffect(() => {
    if (replayGuardDoneRef.current) return;
    if (!adminToken || !backendBase) return;
    const uid =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    if (!uid || !sid || !oid) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: sid,
            organizationId: oid,
            lobby: effectiveLobbyName || "",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !data?.success || !data?.report) return;

        const rpt = data.report;
        // stages.gameover === 1 (or truthy) — session already finished; skip replaying the game.
        const gameover = Number(rpt.gameover);
        if (!Number.isFinite(gameover) || gameover < 1) return;

        replayGuardDoneRef.current = true;
        clearGameProgress();
        const s1 = rpt.stage1 != null ? Number(rpt.stage1) : NaN;
        const s2 = rpt.stage2 != null ? Number(rpt.stage2) : NaN;
        const pts =
          Number(rpt.points) ||
          (Number.isFinite(s1) && Number.isFinite(s2) ? s1 + s2 : 0);
        navigate("/leaderboard", {
          replace: true,
          state: {
            lobbyName: effectiveLobbyName || "",
            points: pts,
            time: rpt.time != null ? String(rpt.time) : "00:00",
            stageId: rpt.id != null ? String(rpt.id) : undefined,
            spellScore: Number.isFinite(s1)
              ? s1
              : Math.max(0, pts - (Number.isFinite(s2) ? s2 : 0)),
            flightScore: Number.isFinite(s2) ? s2 : 0,
          },
        });
      } catch (err) {
        console.error("game replay guard fetchReport:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase, effectiveLobbyName, navigate, storedUserData]);

  /* ── Load questions ── */
  useEffect(() => {
    const loadQuestions = async () => {
      const sessionId = storedUserData.sessionId;
      const organizationId = storedUserData.organizationId;
      const userId =
        storedUserData.userId || storedUserData.userid || storedUserData.id;
      if (!sessionId || !organizationId || !adminToken || !backendBase) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        // Link/QR: sync stage + lobby timer rules before fetchGameQuestions, or first load
        // reads default timer_off and shows forward elapsed until refresh.
        if (!isDemoBypass) {
          await ensureStageAndReportReady();
        }
        const res = await fetch(`${backendBase}/fetchGameQuestions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            sessionId,
            organizationId,
            lobby: effectiveLobbyName || "",
            userId: userId || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.success) {
          throw new Error(data?.message || "Could not load questions.");
        }
        const list = Array.isArray(data.questions) ? data.questions : [];
        if (!list.length) {
          void Swal.fire({
            icon: "warning",
            title: "No Questions Found",
            text: "Please publish questions first.",
          }).then(() => navigate("/question", { replace: true }));
          return;
        }

        const st = data.stage;
        let nextStageRules = null;
        if (st && typeof st === "object") {
          nextStageRules = {
            timer_enabled: toBool(st.timer_status),
            timer_seconds: parseQuestionTimerToSeconds(st.question_timer),
            question_point: stageQuestionPoint(st.question_point, 10),
            points_enabled: pointsEnabledFromStage(st),
          };
        }

        const saved = readGameProgress(gameProgressScope);
        const restoredDeck = saved
          ? restoreDeckFromSavedIds(list, saved.deckIds)
          : null;

        if (restoredDeck) {
          skipQuestionEnterAnimRef.current = true;
          skipPaletteShuffleRef.current = true;
          setDeck(restoredDeck);

          const answeredCount = Array.isArray(saved.answerLog)
            ? saved.answerLog.length
            : 0;
          let nextQIndex = Number.isFinite(Number(saved.qIndex))
            ? Math.max(0, Math.floor(Number(saved.qIndex)))
            : 0;
          if (answeredCount > nextQIndex) {
            nextQIndex = answeredCount;
          }
          nextQIndex = Math.min(nextQIndex, Math.max(0, restoredDeck.length - 1));

          setQIndex(nextQIndex);
          setPoints(
            Number.isFinite(Number(saved.points)) ? Math.max(0, Number(saved.points)) : 0
          );
          setCorrectCount(
            Number.isFinite(Number(saved.correctCount))
              ? Math.max(0, Number(saved.correctCount))
              : 0
          );
          if (Number.isFinite(Number(saved.startedAtMs)) && Number(saved.startedAtMs) > 0) {
            setStartedAtMs(Number(saved.startedAtMs));
          }
          const restoredLog = Array.isArray(saved.answerLog) ? saved.answerLog : [];
          setAnswerLog(restoredLog);
          answerLogRef.current = restoredLog;
          if (
            Array.isArray(saved.optionPaletteOrder) &&
            saved.optionPaletteOrder.length === 4
          ) {
            setOptionPaletteOrder(saved.optionPaletteOrder);
          }
          setStageRules(
            saved.stageRules && typeof saved.stageRules === "object"
              ? saved.stageRules
              : nextStageRules
          );

          if (answeredCount >= restoredDeck.length) {
            pendingCompleteOnRestoreRef.current = true;
          }
        } else {
          setDeck(shuffleQuestions(list));
          setStageRules(nextStageRules);
        }
      } catch (err) {
        console.error(err);
        void Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.message || "Failed to load questions.",
        });
      } finally {
        setLoading(false);
      }
    };
    loadQuestions();
  }, [
    adminToken,
    backendBase,
    effectiveLobbyName,
    ensureStageAndReportReady,
    isDemoBypass,
    navigate,
    storedUserData.organizationId,
    storedUserData.sessionId,
    storedUserData.userId,
    storedUserData.userid,
    storedUserData.id,
    gameProgressScope,
  ]);

  useEffect(() => {
    if (loading || !deck.length || !gameProgressScope) return;
    writeGameProgress(gameProgressScope, {
      deckIds: deck.map((q) => q.id),
      qIndex,
      points,
      correctCount,
      startedAtMs,
      answerLog,
      stageRules,
      optionPaletteOrder,
    });
  }, [
    loading,
    deck,
    gameProgressScope,
    qIndex,
    points,
    correctCount,
    startedAtMs,
    answerLog,
    stageRules,
    optionPaletteOrder,
  ]);

  const row = deck[qIndex] || null;
  const total = deck.length;
  const rowPointValue = useMemo(() => {
    if (stageRules) return stageRules.question_point;
    return toPositiveInt(row?.question_point, 10);
  }, [row, stageRules]);
  const isQuestionTimerEnabled = useMemo(() => {
    if (!row) return false;
    if (stageRules) return stageRules.timer_enabled;
    return toBool(row.timer_status);
  }, [row, stageRules]);
  /** Parsed `question_timer` (MM:SS or seconds) — used only when `timer_status` is true for countdown. */
  const rowTimerSeconds = useMemo(() => {
    if (!row) return 0;
    if (stageRules) return stageRules.timer_seconds;
    return parseQuestionTimerToSeconds(row?.question_timer);
  }, [row, stageRules]);
  const isPointsEnabled = useMemo(() => {
    if (stageRules) return Boolean(stageRules.points_enabled);
    return true;
  }, [stageRules]);

  const totalPossiblePoints = useMemo(() => {
    if (!isPointsEnabled || !deck.length) return 0;
    const perQ = stageRules
      ? stageRules.question_point
      : toPositiveInt(deck[0]?.question_point, 10);
    return deck.length * perQ;
  }, [deck, stageRules, isPointsEnabled]);

  useEffect(() => {
    // Keep ref in sync for callbacks.
    answerLogRef.current = answerLog;
  }, [answerLog]);

  /** Always tick forward timer once per second. */
  useEffect(() => {
    const id = window.setInterval(() => {
      setForwardClockTick((n) => n + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    // Clear any pending auto-advance timer when question changes.
    if (feedbackTimerRef.current) {
      window.clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
    setAnswerFeedback(null);
  }, [qIndex]);

  useEffect(() => {
    if (qIndex === 0) return;
    if (skipPaletteShuffleRef.current) {
      skipPaletteShuffleRef.current = false;
      return;
    }
    setOptionPaletteOrder((prev) => shuffleOptionPalette(prev));
  }, [qIndex]);

  useEffect(() => {
    if (skipQuestionEnterAnimRef.current) {
      skipQuestionEnterAnimRef.current = false;
      return;
    }
    setQuestionEnterClass("quiz-mc-stage--qenter");
    const tid = window.setTimeout(() => setQuestionEnterClass(""), 480);
    return () => window.clearTimeout(tid);
  }, [qIndex]);

  useEffect(() => {
    if (!helpModalOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setHelpModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [helpModalOpen]);

  /**
   * @param {{ points: number; correctCount: number } | null} scoreSnapshot
   *   Pass from the last-question timeout so DB/report get the final totals. Otherwise React state
   *   inside this callback can still be one step behind (stale closure on scheduled timeout).
   */
  const completeGameAndExit = useCallback(async (scoreSnapshot = null) => {
    const userId =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sessionId = storedUserData.sessionId;
    const organizationId = storedUserData.organizationId;
    const elapsedMs = Math.max(0, Date.now() - startedAtMs);
    const elapsedText = formatElapsed(elapsedMs);
    const finalPoints =
      scoreSnapshot && Number.isFinite(Number(scoreSnapshot.points))
        ? Number(scoreSnapshot.points)
        : points;
    const finalCorrect =
      scoreSnapshot && Number.isFinite(Number(scoreSnapshot.correctCount))
        ? Number(scoreSnapshot.correctCount)
        : correctCount;
    const wrongCount = Math.max(0, total - finalCorrect);

    if (backendBase && adminToken && userId && sessionId && organizationId) {
      try {
        setIsFinishing(true);
        await ensureStageAndReportReady();
        const res = await fetch(`${backendBase}/completeGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId,
            sessionId,
            organizationId,
            lobby: effectiveLobbyName || "",
            points: finalPoints,
            total_score: finalPoints,
            total_questions: total,
            correct_count: finalCorrect,
            wrongcount: wrongCount,
            elapsed_ms: elapsedMs,
            time: elapsedText,
            currentQues: String(total),
            stage1: finalPoints,
            user_answer: answerLogRef.current,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.success) {
          throw new Error(data?.message || "Could not save game result.");
        }
        if (data?.id != null && String(data.id).trim() !== "") {
          stageRowIdRef.current = String(data.id).trim();
        }
      } catch (err) {
        console.error(err);
        await Swal.fire({
          icon: "warning",
          title: "Result save failed",
          text:
            err?.message ||
            "Could not save result right now. You can continue.",
        });
      } finally {
        setIsFinishing(false);
      }
    }

    if (!isDemoBypass && sessionId && organizationId && userId) {
      try {
        if (!reportIdRef.current && backendBase && adminToken) {
          try {
            const fr = await fetch(`${backendBase}/fetchReport`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId,
                sessionId,
                organizationId,
                lobby: effectiveLobbyName || "",
              }),
            });
            const frData = await fr.json().catch(() => ({}));
            const rid = frData?.report?.reportId;
            if (rid != null && String(rid).trim() !== "") {
              reportIdRef.current = String(rid).trim();
            }
          } catch (fetchRidErr) {
            console.error("game fetch reportId before final send:", fetchRidErr);
          }
        }

        const reportRes = await sendReport({
          ...(reportIdRef.current ? { reportId: reportIdRef.current } : {}),
          sessionId,
          organizationId,
          role: reportRoleForApi,
          token: storedUserData.token || "",
          points: finalPoints,
          time: elapsedText,
          gameId: storedUserData.gameId,
          name: normalizePlayerName(storedUserData),
          userId,
        });
        const updatedRid = extractReportIdFromResponse(reportRes);
        if (updatedRid) {
          reportIdRef.current = String(updatedRid);
          persistIdsToStoredUserData(
            reportIdRef.current,
            stageRowIdRef.current
          );
        }
        if (
          updatedRid &&
          stageRowIdRef.current &&
          backendBase &&
          adminToken
        ) {
          try {
            await fetch(`${backendBase}/welcomeStageLinkReport`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId,
                sessionId,
                organizationId,
                stageId: stageRowIdRef.current,
                reportId: String(updatedRid),
              }),
            });
          } catch (linkErr) {
            console.error("game welcomeStageLinkReport:", linkErr);
          }
        }
      } catch (err) {
        console.error("game sendReport:", err);
      }
    }

    // replace: false keeps /game in history so browser Back from leaderboard can return here;
    // replay guard above redirects back to /leaderboard when stages.gameover is set.
    clearGameProgress();

    navigate("/leaderboard", {
      replace: false,
      state: {
        lobbyName: effectiveLobbyName || "",
        points: finalPoints,
        maxScore: totalPossiblePoints,
        spellScore: finalPoints,
        stage1: finalPoints,
        wrongcount: wrongCount,
        time: elapsedText,
      },
    });
  }, [
    adminToken,
    backendBase,
    correctCount,
    effectiveLobbyName,
    ensureStageAndReportReady,
    isDemoBypass,
    navigate,
    persistIdsToStoredUserData,
    points,
    reportRoleForApi,
    startedAtMs,
    storedUserData,
    total,
    totalPossiblePoints,
  ]);

  useEffect(() => {
    if (!pendingCompleteOnRestoreRef.current || loading || !deck.length) return;
    if (answerLog.length < deck.length) return;
    pendingCompleteOnRestoreRef.current = false;
    void completeGameAndExit({ points, correctCount });
  }, [loading, deck, answerLog, points, correctCount, completeGameAndExit]);

  const submitChoice = useCallback(
    async (selectedIdx) => {
      if (!row || isCommitting || isFinishing) return;
      if (answerFeedback) return;
      const opts = rowOptionTexts(row);
      const correctIdx0 = correctAnswerIndex(row);
      const timedOut = selectedIdx == null;
      const hasAnswer = correctIdx0 !== null;
      const isCorrect =
        !timedOut && hasAnswer && selectedIdx === correctIdx0;
      const correctLabel = hasAnswer && opts[correctIdx0] ? opts[correctIdx0] : "";
      const selectedLabel =
        selectedIdx != null && selectedIdx >= 0 && selectedIdx <= 3
          ? opts[selectedIdx] || ""
          : "";
      const nextPoints =
        points + (isPointsEnabled && isCorrect ? rowPointValue : 0);
      const isLast = qIndex + 1 >= total;

      setIsCommitting(true);
      try {
        const status = timedOut
          ? "timeout"
          : !hasAnswer
            ? "noanswer"
            : isCorrect
              ? "correct"
              : "wrong";

        setPoints(nextPoints);
        if (isCorrect) setCorrectCount((v) => v + 1);

        const nextCorrectCount = correctCount + (isCorrect ? 1 : 0);
        setAnswerLog((prev) => {
          const next = [
            ...prev,
            {
              question_id: row.id,
              question_name: String(row.question_name || ""),
              options: opts,
              correct_option: correctLabel,
              user_answer: timedOut ? null : selectedLabel,
              correct_index: correctIdx0,
              user_index: timedOut ? null : selectedIdx,
              is_correct: Boolean(hasAnswer && !timedOut && isCorrect),
              timestamp: formatIndianTimestamp(),
            },
          ];
          answerLogRef.current = next;
          return next;
        });

        setAnswerFeedback({
          selectedIdx,
          correctIdx0,
          status,
        });

        if (status === "correct" && isPointsEnabled) {
          fireConfettiBurst();
        }

        setIsCommitting(false);

        if (isDemoBypass && qIndex === 0 && !demoRedirectTriggeredRef.current) {
          demoRedirectTriggeredRef.current = true;
          window.setTimeout(() => {
            void Swal.fire({
              icon: "info",
              text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
              confirmButtonText: "OK",
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then(() => {
              window.location.assign("https://extramileplay.com/plans");
            });
          }, 400);
        }

        const advanceDelay = !isPointsEnabled
          ? FEEDBACK_ADVANCE_SCORING_OFF_MS
          : status === "correct"
            ? FEEDBACK_BEFORE_ADVANCE_MS.correct
            : status === "wrong"
              ? FEEDBACK_BEFORE_ADVANCE_MS.wrong
              : FEEDBACK_BEFORE_ADVANCE_MS.other;

        feedbackTimerRef.current = window.setTimeout(() => {
          if (isLast) {
            void completeGameAndExit({
              points: nextPoints,
              correctCount: nextCorrectCount,
            });
            return;
          }
          setQIndex((i) => i + 1);
        }, advanceDelay);
      } catch {
        setIsCommitting(false);
      }
    },
    [
      row,
      isCommitting,
      isFinishing,
      answerFeedback,
      points,
      correctCount,
      qIndex,
      total,
      rowPointValue,
      isPointsEnabled,
      isDemoBypass,
      completeGameAndExit,
    ]
  );

  const showGameHelp = useCallback(() => {
    setHelpModalOpen(true);
  }, []);

  if (loading || !row) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  const optionLabels = rowOptionTexts(row);
  const timerDisplay = formatElapsed(Date.now() - startedAtMs).replace(":", ".");
  const correctIdx0 = correctAnswerIndex(row);
  const selectedIdx = answerFeedback?.selectedIdx ?? null;

  return (
    <div className="quiz-mc-root">
      <div className="quiz-mc-decor" aria-hidden="true">
        <span className="quiz-mc-blob quiz-mc-blob--a" />
        <span className="quiz-mc-blob quiz-mc-blob--b" />
        <span className="quiz-mc-blob quiz-mc-blob--c" />
        <span className="quiz-mc-spark quiz-mc-spark--1" />
        <span className="quiz-mc-spark quiz-mc-spark--2" />
        <span className="quiz-mc-spark quiz-mc-spark--3" />
      </div>

      <div className="quiz-mc-surface">
        <header className="quiz-mc-header">
          <div className="quiz-mc-header-left">
            <div className="quiz-mc-chip quiz-mc-chip--timer">
              <span className="quiz-mc-chip-icon-wrap" aria-hidden="true">
                <img src={timerIcon} alt="" className="quiz-mc-chip-icon" />
              </span>
              <span className="quiz-mc-chip-value">{timerDisplay}</span>
            </div>
            <button
              type="button"
              className="quiz-mc-help"
              onClick={showGameHelp}
              aria-label="Help"
            >
              ?
            </button>
          </div>
          {isPointsEnabled ? (
            <div className="quiz-mc-chip quiz-mc-chip--score">
              <span className="quiz-mc-chip-icon-wrap" aria-hidden="true">
                <img src={scoreIcon} alt="" className="quiz-mc-chip-icon" />
              </span>
              <span className="quiz-mc-chip-value">{points}</span>
            </div>
          ) : null}
        </header>

        <div
          className={`quiz-mc-stage${
            isPointsEnabled && answerFeedback ? " quiz-mc-stage--feedback" : ""
          } ${questionEnterClass}`.trim()}
        >
          <button
            type="button"
            className={`quiz-mc-opt quiz-mc-opt--1 ${OPTION_PALETTE_CLASSES[optionPaletteOrder[0]]} ${
              !optionLabels[0] ? "is-empty" : ""
            } ${
              isPointsEnabled && answerFeedback && correctIdx0 === 0 ? "is-correct" : ""
            } ${
              isPointsEnabled && answerFeedback && answerFeedback.status === "wrong" && selectedIdx === 0
                ? "is-wrong"
                : ""
            }`}
            disabled={isCommitting || !!answerFeedback || !optionLabels[0]}
            onClick={() => void submitChoice(0)}
          >
            {optionLabels[0] || "—"}
          </button>
          <button
            type="button"
            className={`quiz-mc-opt quiz-mc-opt--2 ${OPTION_PALETTE_CLASSES[optionPaletteOrder[1]]} ${
              !optionLabels[1] ? "is-empty" : ""
            } ${
              isPointsEnabled && answerFeedback && correctIdx0 === 1 ? "is-correct" : ""
            } ${
              isPointsEnabled && answerFeedback && answerFeedback.status === "wrong" && selectedIdx === 1
                ? "is-wrong"
                : ""
            }`}
            disabled={isCommitting || !!answerFeedback || !optionLabels[1]}
            onClick={() => void submitChoice(1)}
          >
            {optionLabels[1] || "—"}
          </button>
          <div className="quiz-mc-question-box">
            <div
              className="quiz-mc-question-text mf-q-html"
              dangerouslySetInnerHTML={{
                __html: String(row.question_name || ""),
              }}
            />
          </div>
          <button
            type="button"
            className={`quiz-mc-opt quiz-mc-opt--3 ${OPTION_PALETTE_CLASSES[optionPaletteOrder[2]]} ${
              !optionLabels[2] ? "is-empty" : ""
            } ${
              isPointsEnabled && answerFeedback && correctIdx0 === 2 ? "is-correct" : ""
            } ${
              isPointsEnabled && answerFeedback && answerFeedback.status === "wrong" && selectedIdx === 2
                ? "is-wrong"
                : ""
            }`}
            disabled={isCommitting || !!answerFeedback || !optionLabels[2]}
            onClick={() => void submitChoice(2)}
          >
            {optionLabels[2] || "—"}
          </button>
          <button
            type="button"
            className={`quiz-mc-opt quiz-mc-opt--4 ${OPTION_PALETTE_CLASSES[optionPaletteOrder[3]]} ${
              !optionLabels[3] ? "is-empty" : ""
            } ${
              isPointsEnabled && answerFeedback && correctIdx0 === 3 ? "is-correct" : ""
            } ${
              isPointsEnabled && answerFeedback && answerFeedback.status === "wrong" && selectedIdx === 3
                ? "is-wrong"
                : ""
            }`}
            disabled={isCommitting || !!answerFeedback || !optionLabels[3]}
            onClick={() => void submitChoice(3)}
          >
            {optionLabels[3] || "—"}
          </button>
        </div>
      </div>

      {helpModalOpen ? (
        <div
          className="quiz-help-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setHelpModalOpen(false);
          }}
        >
          <div
            className="quiz-help-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quiz-help-heading"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="quiz-help-head">
              <h2 id="quiz-help-heading" className="quiz-help-heading">
                How to play
              </h2>
              <button
                type="button"
                className="quiz-help-x"
                onClick={() => setHelpModalOpen(false)}
                aria-label="Close"
              >
                <svg
                  className="quiz-help-x-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.25"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="quiz-help-body">
              <ol className="quiz-help-rules">
                {HOW_TO_PLAY_RULES.map((line, i) => (
                  <li key={`${i}-${line}`} className="quiz-help-rule">
                    <span className="quiz-help-step" aria-hidden="true">
                      {i + 1}
                    </span>
                    <span className="quiz-help-rule-text">{line}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="quiz-help-foot">
              <button type="button" className="quiz-help-gotit" onClick={() => setHelpModalOpen(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Game;
